<template>
  <div class="automated-assessment-dashboard-page">
    <div class="dashboard-page-header">
      <h2 class="assessment-title">Dashboard</h2>
    </div>

    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <BaseSpinner />
      </div>

      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button class="retry-btn" @click="loadTrendsContext">Retry</button>
      </div>

      <div v-else-if="trendsContext" class="dashboard-grid">
        <!-- Monthly Sales Trend -->
        <section class="dash-card">
          <h3 class="dash-card-title">Monthly Sales Trend (In AED)</h3>

          <div v-if="chartBars.length" class="sales-chart">
            <div class="sales-chart-y-axis">
              <span
                v-for="(tick, index) in yAxisTicks"
                :key="`y-tick-${index}`"
              >{{ formatChartTick(tick) }}</span>
            </div>
            <div class="sales-chart-body">
              <div class="sales-chart-plot">
                <div class="sales-chart-grid">
                  <div
                    v-for="(tick, index) in yAxisTicks"
                    :key="`grid-${index}`"
                    class="sales-chart-grid-line"
                  ></div>
                </div>
                <div class="sales-chart-bars">
                  <div
                    v-for="(bar, index) in chartBars"
                    :key="`bar-${index}`"
                    class="sales-bar-column"
                  >
                    <div
                      class="sales-bar-fill"
                      :style="{ height: `${bar.heightPct}%` }"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="sales-chart-x-labels">
                <span
                  v-for="(bar, index) in chartBars"
                  :key="`label-${index}`"
                  class="sales-bar-label"
                >{{ bar.label }}</span>
              </div>
            </div>
          </div>
          <div v-else class="chart-empty">No sales trend data.</div>

          <ul class="sales-metrics-list">
            <li
              v-for="(metric, index) in salesMetrics"
              :key="`metric-${index}`"
              class="sales-metric-item"
            >
              <span class="sales-metric-check" aria-hidden="true">✓</span>
              <span>
                <strong>{{ metric.label }}</strong>
                <span v-if="metric.value !== '—'"> - {{ metric.value }}</span>
              </span>
            </li>
          </ul>
        </section>

        <!-- Cash / WCR ratio trends -->
        <section class="dash-card">
          <h3 class="dash-card-title">Cash Ratio &amp; Working Capital Ratio Trends</h3>

          <div v-if="ratioTrendChart.hasData" class="ratio-trend-chart">
            <div class="ratio-chart-y-axis-wrap ratio-chart-y-axis-wrap-left">
              <div class="ratio-axis-title-stack">
                <span>Cash</span>
                <span>Ratio</span>
              </div>
              <div class="ratio-chart-y-axis ratio-chart-y-axis-left">
                <span
                  v-for="(tick, index) in ratioTrendChart.cashYTicks"
                  :key="`cash-y-${index}`"
                >{{ formatRatioTick(tick) }}</span>
              </div>
            </div>

            <div class="ratio-chart-main">
              <svg
                class="ratio-chart-svg"
                :viewBox="`0 0 ${ratioTrendChart.plotWidth} ${ratioTrendChart.plotHeight}`"
                preserveAspectRatio="none"
              >
                <g class="ratio-chart-grid-lines">
                  <line
                    v-for="(tick, index) in ratioTrendChart.cashYTicks"
                    :key="`cash-grid-${index}`"
                    :x1="0"
                    :y1="ratioTrendChart.yCash(tick)"
                    :x2="ratioTrendChart.plotWidth"
                    :y2="ratioTrendChart.yCash(tick)"
                  />
                </g>
                <path
                  v-if="ratioTrendChart.wcrPath"
                  class="ratio-line ratio-line-wcr"
                  :d="ratioTrendChart.wcrPath"
                />
                <path
                  v-if="ratioTrendChart.cashPath"
                  class="ratio-line ratio-line-cash"
                  :d="ratioTrendChart.cashPath"
                />
                <circle
                  v-for="(point, index) in ratioTrendChart.wcrPoints"
                  :key="`wcr-pt-${index}`"
                  class="ratio-dot ratio-dot-wcr"
                  :cx="point.x"
                  :cy="point.y"
                  r="3.5"
                />
                <circle
                  v-for="(point, index) in ratioTrendChart.cashPoints"
                  :key="`cash-pt-${index}`"
                  class="ratio-dot ratio-dot-cash"
                  :cx="point.x"
                  :cy="point.y"
                  r="3.5"
                />
              </svg>

              <div class="ratio-chart-x-labels">
                <span
                  v-for="(label, index) in ratioTrendChart.labels"
                  :key="`ratio-label-${index}`"
                  class="ratio-chart-x-label"
                >{{ label }}</span>
              </div>

              <div class="ratio-chart-legend">
                <span class="ratio-legend-item">
                  <span class="ratio-legend-swatch ratio-legend-swatch-wcr"></span>
                  Working Capital Ratio
                </span>
                <span class="ratio-legend-item">
                  <span class="ratio-legend-swatch ratio-legend-swatch-cash"></span>
                  Cash Ratio
                </span>
              </div>
            </div>

            <div class="ratio-chart-y-axis-wrap ratio-chart-y-axis-wrap-right">
              <div class="ratio-chart-y-axis ratio-chart-y-axis-right">
                <span
                  v-for="(tick, index) in ratioTrendChart.wcrYTicks"
                  :key="`wcr-y-${index}`"
                >{{ formatRatioTick(tick) }}</span>
              </div>
              <div class="ratio-axis-title-stack">
                <span>Working</span>
                <span>Capital</span>
                <span>Ratio</span>
              </div>
            </div>
          </div>
          <div v-else class="chart-empty">No ratio trend data.</div>
        </section>

        <div class="dash-row">
          <!-- Yearly Sales Trend -->
          <section class="dash-card">
            <h3 class="dash-card-title">Three-year Sales Trend (In mAED)</h3>
            <div v-if="yearlySalesChart.hasData" class="yearly-sales-chart">
              <div class="yearly-sales-plot-row">
                <div class="yearly-sales-y-axis">
                  <span
                    v-for="(tick, index) in yearlySalesChart.yTicks"
                    :key="`yearly-y-${index}`"
                  >{{ yearlySalesChart.formatTick(tick) }}</span>
                </div>
                <div class="yearly-sales-plot">
                  <div class="yearly-sales-grid">
                    <div
                      v-for="(tick, index) in yearlySalesChart.yTicks"
                      :key="`yearly-grid-${index}`"
                      class="yearly-sales-grid-line"
                    ></div>
                  </div>
                  <svg
                    class="yearly-sales-trend-svg"
                    :viewBox="`0 0 ${yearlySalesChart.plotWidth} ${yearlySalesChart.plotHeight}`"
                    preserveAspectRatio="none"
                  >
                    <path
                      v-if="yearlySalesChart.trendPath"
                      class="yearly-sales-trend-line"
                      :d="yearlySalesChart.trendPath"
                    />
                  </svg>
                  <div class="yearly-sales-bars">
                    <div
                      v-for="(bar, index) in yearlySalesChart.bars"
                      :key="`yearly-bar-${index}`"
                      class="yearly-sales-column"
                    >
                      <div
                        class="yearly-sales-bar-fill"
                        :style="{ height: `${bar.heightPct}%` }"
                      >
                        <span class="yearly-sales-bar-value">{{ bar.displayValue }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="yearly-sales-x-labels">
                <span class="yearly-sales-y-spacer" aria-hidden="true"></span>
                <span
                  v-for="(bar, index) in yearlySalesChart.bars"
                  :key="`yearly-label-${index}`"
                  class="yearly-sales-x-label"
                >{{ bar.label }}</span>
              </div>
            </div>
            <div v-else class="chart-empty">No yearly sales trend data.</div>
          </section>

          <!-- Payer concentration -->
          <section class="dash-card">
            <h3 class="dash-card-title">Top 5 Payer Sales &amp; Industry Concentration</h3>
            <div v-if="payerConcentrationChart.hasData" class="dash-apex-chart">
              <apexchart
                type="bar"
                :height="payerConcentrationChart.height"
                :options="payerConcentrationChart.options"
                :series="payerConcentrationChart.series"
              />
            </div>
            <div v-else class="chart-empty">No payer concentration data.</div>
          </section>
        </div>

        <div class="dash-row">
          <!-- Receivables payer concentration -->
          <section class="dash-card">
            <h3 class="dash-card-title">Receivables Payer Concentration</h3>
            <div v-if="recOutstandingChart.hasData" class="dash-apex-chart">
              <apexchart
                type="bar"
                :height="recOutstandingChart.height"
                :options="recOutstandingChart.options"
                :series="recOutstandingChart.series"
              />
            </div>
            <div v-else class="chart-empty">No receivables concentration data.</div>
          </section>

          <!-- Aging buckets -->
          <section class="dash-card">
            <h3 class="dash-card-title">Receivables Ageing Analysis</h3>
            <div v-if="agingBucketChart.hasData" class="dash-apex-chart">
              <apexchart
                type="bar"
                :height="agingBucketChart.height"
                :options="agingBucketChart.options"
                :series="agingBucketChart.series"
              />
            </div>
            <div v-else class="chart-empty">No ageing data.</div>
          </section>
        </div>

        <div class="dash-row">
          <!-- Risk performance -->
          <section class="dash-card dash-card--center-chart">
            <h3 class="dash-card-title">Relative Risk Performance</h3>
            <div v-if="riskPerformanceChart.hasData" class="dash-apex-chart">
              <apexchart
                type="bar"
                width="100%"
                :height="riskPerformanceChart.height"
                :options="riskPerformanceChart.options"
                :series="riskPerformanceChart.series"
              />
            </div>
            <div v-else class="chart-empty">No risk summary data.</div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';

const RISK_SUMMARY_CATEGORIES = [
  { key: 'Operations', label: 'Operations Risk' },
  { key: 'Liquidity', label: 'Liquidity Risk' },
  { key: 'Mgmt, Bus & Ind.', label: 'Management, business, & Industrial Risk' },
  { key: 'Third Party', label: 'Repayment Risk' }
];

export default {
  name: 'AutomatedAssessmentDashboard',
  components: { BaseSpinner },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      trendsContext: null
    };
  },
  computed: {
    monthlySalesTrend() {
      const trend = this.trendsContext?.monthly_sales_trend;
      if (!Array.isArray(trend)) return [];
      return trend.map((item) => ({
        label: item.month || item.label || '—',
        value: item.value
      }));
    },
    chartBars() {
      const trend = this.monthlySalesTrend;
      if (!trend.length) return [];

      const values = trend.map((item) => Number(item.value) || 0);
      const maxValue = Math.max(...values, 1);
      const yMax = Math.max(Math.ceil(maxValue / 500000) * 500000, 500000);

      return trend.map((item) => ({
        label: item.label || '—',
        heightPct: Math.round(((Number(item.value) || 0) / yMax) * 100)
      }));
    },
    yAxisTicks() {
      const values = this.monthlySalesTrend.map((item) => Number(item.value) || 0);
      const maxValue = Math.max(...values, 1);
      const yMax = Math.max(Math.ceil(maxValue / 500000) * 500000, 500000);
      return [yMax, Math.round((yMax * 2) / 3), Math.round(yMax / 3), 0];
    },
    salesMetrics() {
      const ctx = this.trendsContext || {};
      return [
        { label: 'Average Sales (monthly)', value: this.formatCurrency(ctx.average_monthly_sales) },
        { label: 'Min Sales', value: this.formatCurrency(ctx.min_sales) },
        { label: 'Max Sales', value: this.formatCurrency(ctx.max_sales) }
      ];
    },
    yearlySalesChart() {
      const trend = Array.isArray(this.trendsContext?.yearly_sales_trend)
        ? this.trendsContext.yearly_sales_trend
        : [];

      if (!trend.length) {
        return { hasData: false };
      }

      const values = trend.map((item) => Number(item.value) || 0);
      const yMax = this.buildRatioAxisMax(values, 10);
      const yTicks = this.buildRatioAxisTicks(yMax, 5);
      const plotWidth = 540;
      const plotHeight = 180;
      const pointCount = trend.length;
      const xAt = (index) => (
        pointCount === 1
          ? plotWidth / 2
          : ((index + 0.5) / pointCount) * plotWidth
      );
      const yAt = (value) => plotHeight - ((Number(value) || 0) / yMax) * plotHeight;
      const points = trend.map((item, index) => ({
        x: xAt(index),
        y: yAt(item.value)
      }));

      return {
        hasData: true,
        plotWidth,
        plotHeight,
        yTicks,
        trendPath: this.buildLinePath(points),
        formatTick: (tick) => {
          const number = Number(tick);
          if (!Number.isFinite(number)) return '0';
          return Number.isInteger(number) ? String(number) : String(number);
        },
        bars: trend.map((item) => {
          const value = Number(item.value) || 0;
          return {
            label: item.label || '—',
            value,
            heightPct: yMax ? (value / yMax) * 100 : 0,
            displayValue: Number.isInteger(value) ? String(value) : String(value)
          };
        })
      };
    },
    ratioTrendChart() {
      const rows = Array.isArray(this.trendsContext?.ratio_trends)
        ? this.trendsContext.ratio_trends
        : [];

      if (!rows.length) return { hasData: false };

      const cashTrend = rows.map((row) => ({
        label: row.label || '—',
        value: row.cash_ratio
      }));
      const wcrTrend = rows.map((row) => ({
        label: row.label || '—',
        value: row.working_capital_ratio
      }));

      const cashValues = cashTrend.map((point) => Number(point.value) || 0);
      const wcrValues = wcrTrend.map((point) => Number(point.value) || 0);
      const cashMax = this.buildRatioAxisMax(cashValues, 1);
      const wcrMax = this.buildRatioAxisMax(wcrValues, 1);
      const plotWidth = 1000;
      const plotHeight = 160;
      const padX = plotWidth / (rows.length * 2);
      const padY = 6;
      const labels = rows.map((row) => row.label || '—');
      const pointCount = rows.length;

      const xAt = (index) => (
        pointCount === 1
          ? plotWidth / 2
          : padX + (index / (pointCount - 1)) * (plotWidth - padX * 2)
      );
      const yCash = (value) => padY + (1 - ((Number(value) || 0) / cashMax)) * (plotHeight - padY * 2);
      const yWcr = (value) => padY + (1 - ((Number(value) || 0) / wcrMax)) * (plotHeight - padY * 2);

      const cashPoints = cashTrend.map((point, index) => ({
        x: xAt(index),
        y: yCash(point.value)
      }));
      const wcrPoints = wcrTrend.map((point, index) => ({
        x: xAt(index),
        y: yWcr(point.value)
      }));

      return {
        hasData: Boolean(cashPoints.length || wcrPoints.length),
        labels,
        cashPoints,
        wcrPoints,
        cashPath: this.buildLinePath(cashPoints),
        wcrPath: this.buildLinePath(wcrPoints),
        plotWidth,
        plotHeight,
        cashYTicks: this.buildRatioAxisTicks(cashMax),
        wcrYTicks: this.buildRatioAxisTicks(wcrMax),
        yCash,
        yWcr
      };
    },
    payerConcentrationChart() {
      return this.buildApexBarChart(
        this.trendsContext?.payer_concentration_trend,
        { scaleType: 'percent', color: '#ed7d31' }
      );
    },
    agingBucketChart() {
      return this.buildApexBarChart(
        this.trendsContext?.aging_bucket_trend,
        { scaleType: 'currency', color: '#4472c4' }
      );
    },
    recOutstandingChart() {
      return this.buildApexBarChart(
        this.trendsContext?.rec_oustanding_cust_trend
          || this.trendsContext?.rec_outstanding_cust_trend,
        { scaleType: 'percent', color: '#ed7d31' }
      );
    },
    riskPerformanceChart() {
      const summary = this.trendsContext?.risk_summary || {};
      const hasSummaryData = RISK_SUMMARY_CATEGORIES.some(({ key }) => summary[key]);
      if (!hasSummaryData) return { hasData: false };

      const trend = RISK_SUMMARY_CATEGORIES.map(({ key, label }) => {
        const pct = Number(summary[key]?.pct);
        return {
          label,
          value: Number.isFinite(pct) ? pct * 100 : 0
        };
      });

      return this.buildApexBarChart(trend, {
        scaleType: 'percent',
        color: '#4472c4',
        showValues: true,
        xMax: 100,
        height: 400,
        labelMaxWidth: 108,
        wrapYLabels: true
      });
    }
  },
  mounted() {
    this.loadTrendsContext();
  },
  methods: {
    async loadTrendsContext() {
      if (!this.candidateId) {
        this.error = 'Candidate ID is required';
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        this.trendsContext = await ChatbotService.getReportTrendsContext(this.candidateId);
      } catch (err) {
        console.error('Error loading report trends context:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to load dashboard data.';
        this.trendsContext = null;
      } finally {
        this.loading = false;
      }
    },
    wrapAxisLabel(label, maxChars = 16) {
      const text = String(label || '').trim();
      if (text.length <= maxChars) return text;

      const breakAt = text.lastIndexOf(' ', maxChars);
      const splitIndex = breakAt > 6 ? breakAt : maxChars;
      const first = text.slice(0, splitIndex).trim();
      const rest = text.slice(splitIndex).trim();

      if (!rest) return first;
      if (rest.length <= maxChars) return `${first}\n${rest}`;

      return `${first}\n${this.wrapAxisLabel(rest, maxChars)}`;
    },
    formatCurrency(value) {
      if (value === undefined || value === null || value === '') return '—';
      const number = Number(value);
      if (!Number.isFinite(number)) return '—';
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatChartTick(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return '0';
      return number.toLocaleString('en-US');
    },
    formatRatioTick(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return '0';
      if (Number.isInteger(number)) return String(number);
      return number.toFixed(1);
    },
    buildRatioAxisMax(values, fallback = 1) {
      const numericValues = (Array.isArray(values) ? values : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

      const maxValue = numericValues.length ? Math.max(...numericValues, 0) : 0;
      if (maxValue <= 0) return fallback;

      const padded = maxValue * 1.08;
      const magnitude = 10 ** Math.floor(Math.log10(padded));
      const normalized = padded / magnitude;

      let nice;
      if (normalized <= 1) nice = 1;
      else if (normalized <= 2) nice = 2;
      else if (normalized <= 2.5) nice = 2.5;
      else if (normalized <= 5) nice = 5;
      else nice = 10;

      return nice * magnitude;
    },
    buildRatioAxisTicks(max, divisions = 4) {
      const ticks = [];
      for (let index = divisions; index >= 0; index -= 1) {
        ticks.push(Math.round(((max / divisions) * index) * 100) / 100);
      }
      return ticks;
    },
    buildApexBarChart(trend, {
      scaleType = 'percent',
      color = '#ed7d31',
      showValues = false,
      xMax = null,
      height = null,
      labelMaxWidth = 168,
      wrapYLabels = false
    } = {}) {
      if (!Array.isArray(trend) || !trend.length) {
        return { hasData: false };
      }

      const labels = trend.map((item) => {
        const label = item.label || '—';
        return wrapYLabels ? this.wrapAxisLabel(label, 16) : label;
      });
      const values = trend.map((item) => Number(item.value) || 0);
      const maxValue = Math.max(...values, 0);
      let axisMax = xMax;

      if (axisMax == null) {
        axisMax = scaleType === 'currency'
          ? Math.max(Math.ceil(maxValue / 500000) * 500000, 500000)
          : Math.max(Math.ceil(maxValue / 5) * 5, 5);
      }

      const formatTick = (value) => {
        const number = Number(value);
        if (!Number.isFinite(number)) return scaleType === 'currency' ? '0' : '0.00%';
        if (scaleType === 'currency') return number.toLocaleString('en-US');
        return `${number.toFixed(2)}%`;
      };

      return {
        hasData: true,
        height: height || Math.max(220, trend.length * 46 + 52),
        series: [{ name: '', data: values }],
        options: {
          chart: {
            type: 'bar',
            width: '100%',
            toolbar: { show: false },
            parentHeightOffset: 0,
            animations: { enabled: false }
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: '58%',
              borderRadius: 3
            }
          },
          colors: [color],
          dataLabels: {
            enabled: showValues,
            formatter: formatTick,
            style: {
              fontSize: '11px',
              fontWeight: 600,
              colors: ['#ffffff']
            }
          },
          grid: {
            borderColor: '#e2e8f0',
            strokeDashArray: 0,
            padding: { top: -8, right: 16, bottom: 0, left: 8 },
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: false } }
          },
          xaxis: {
            categories: labels,
            max: axisMax,
            tickAmount: 4,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
              formatter: formatTick,
              style: { fontSize: '11px', colors: '#64748b' }
            }
          },
          yaxis: {
            labels: {
              minWidth: 0,
              maxWidth: labelMaxWidth,
              trim: !wrapYLabels,
              align: 'right',
              style: { fontSize: '11px', colors: '#334155' }
            }
          },
          tooltip: {
            y: { formatter: formatTick }
          },
          legend: { show: false }
        }
      };
    },
    buildLinePath(points) {
      if (!Array.isArray(points) || !points.length) return '';
      return points.map((point, index) => {
        const command = index === 0 ? 'M' : 'L';
        return `${command} ${point.x} ${point.y}`;
      }).join(' ');
    }
  }
};
</script>

<style lang="scss" scoped>
@import './index.scss';

.automated-assessment-dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  min-height: 100%;
}

.dashboard-page-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eef1f6;
}

.assessment-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #101828;
  font-family: $brand-font-bold;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
  color: #4a5568;
  gap: 0.75rem;
}

.error-state p {
  color: #e53e3e;
  font-size: 1rem;
  margin: 0 0 1rem;
}

.retry-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-family: $brand-font-medium;

  &:hover {
    opacity: 0.9;
  }
}

.dashboard-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.dash-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.dash-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem 1.35rem;

  &--center-chart {
    display: flex;
    flex-direction: column;
    min-height: 100%;

    .dash-apex-chart,
    .chart-empty {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      min-width: 0;
    }
  }
}

.dash-card-title {
  margin: 0 0 1rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #101828;
  font-family: $brand-font-bold;
}

.chart-empty {
  margin: 0.5rem 0;
  font-size: 0.875rem;
  color: #94a3b8;
}

.sales-chart {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  min-height: 220px;
}

.sales-chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 72px;
  height: 168px;
  margin-top: 12px;
  font-size: 10px;
  color: #374151;
  text-align: right;
}

.sales-chart-body {
  position: relative;
  flex: 1;
  min-width: 0;
}

.sales-chart-plot {
  position: relative;
  height: 180px;
  padding-top: 12px;
  box-sizing: border-box;
}

.sales-chart-grid {
  position: absolute;
  top: 12px;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.sales-chart-grid-line {
  border-top: 1px solid #d1d5db;

  &:last-child {
    border-top: none;
    border-bottom: 1px solid #d1d5db;
  }
}

.sales-chart-bars {
  position: absolute;
  top: 12px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.6px;
  padding: 0 1.6px;
}

.sales-bar-column {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.sales-bar-fill {
  width: 25px;
  max-width: 100%;
  background: #2874E9;
  min-height: 0;
}

.sales-chart-x-labels {
  display: flex;
  justify-content: space-between;
  gap: 1.6px;
  padding: 6px 1.6px 0;
}

.sales-bar-label {
  flex: 1;
  min-width: 0;
  font-size: 9px;
  color: #374151;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
}

.sales-metrics-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #334155;
}

.sales-metric-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
}

.yearly-sales-chart {
  display: flex;
  flex-direction: column;
  margin-top: 44px;
  margin-right: 24px;
}

.yearly-sales-plot-row {
  display: flex;
  gap: 10px;
  height: 180px;
}

.yearly-sales-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 28px;
  flex-shrink: 0;
  font-size: 11px;
  color: #374151;
  text-align: right;
}

.yearly-sales-plot {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 180px;
  box-sizing: border-box;
}

.yearly-sales-grid {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.yearly-sales-grid-line {
  border-top: 1px solid #d1d5db;

  &:last-child {
    border-top: none;
    border-bottom: 1px solid #d1d5db;
  }
}

.yearly-sales-trend-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  z-index: 2;
  pointer-events: none;
}

.yearly-sales-trend-line {
  fill: none;
  stroke: #ed7d31;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

.yearly-sales-bars {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
}

.yearly-sales-column {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.yearly-sales-bar-fill {
  position: relative;
  width: 56px;
  max-width: 70%;
  background: #2874E9;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8px;
}

.yearly-sales-bar-value {
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
}

.yearly-sales-x-labels {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
}

.yearly-sales-y-spacer {
  width: 28px;
  margin-right: 10px;
  flex-shrink: 0;
}

.yearly-sales-x-label {
  flex: 1;
  font-size: 11px;
  color: #374151;
  text-align: center;
  line-height: 1.3;
}

.sales-metric-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-top: 1px;
  background: #2874E9;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.ratio-trend-chart {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.ratio-chart-y-axis-wrap {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 12px;
  height: 160px;
}

.ratio-chart-y-axis-wrap-left,
.ratio-chart-y-axis-wrap-right {
  gap: 8px;
}

.ratio-axis-title-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  width: 18px;
  font-size: 10px;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
}

.ratio-chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  font-size: 11px;
  color: #374151;
  flex-shrink: 0;
}

.ratio-chart-y-axis-left {
  min-width: 28px;
  text-align: right;
}

.ratio-chart-y-axis-right {
  min-width: 28px;
  text-align: left;
}

.ratio-chart-main {
  flex: 1;
  min-width: 0;
}

.ratio-chart-svg {
  display: block;
  width: 100%;
  height: 160px;
  margin-top: 12px;
  overflow: visible;
}

.ratio-line {
  fill: none;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

.ratio-line-wcr {
  stroke: #4f81bd;
}

.ratio-line-cash {
  stroke: #ed7d31;
}

.ratio-dot {
  vector-effect: non-scaling-stroke;
  stroke: #ffffff;
  stroke-width: 1.25;
}

.ratio-dot-wcr {
  fill: #4f81bd;
}

.ratio-dot-cash {
  fill: #ed7d31;
}

.ratio-chart-grid-lines line {
  stroke: #d1d5db;
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

.ratio-chart-x-labels {
  display: flex;
  justify-content: space-between;
  gap: 2px;
  margin-top: 6px;
}

.ratio-chart-x-label {
  flex: 1;
  min-width: 0;
  font-size: 10px;
  color: #374151;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
}

.ratio-chart-legend {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 8px;
  font-size: 10px;
  color: #374151;
}

.ratio-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ratio-legend-swatch {
  width: 18px;
  height: 3px;
  border-radius: 1px;
}

.ratio-legend-swatch-wcr {
  background: #4f81bd;
}

.ratio-legend-swatch-cash {
  background: #ed7d31;
}

.dash-apex-chart {
  width: 100%;
  min-width: 0;

  ::v-deep .vue-apexcharts,
  ::v-deep .apexcharts-canvas {
    width: 100% !important;
    max-width: 100%;
  }
}
</style>
